import {
    BlurEvent, Button, ClickEvent, DataDisplayEvent, DataSource, DataSourceMode, DataSourceModeChangeEvent,
    DropdownSelectionEvent, LookupModelSelectionEvent, TableRow
} from "@mcleod/components";
import { Api, Currency, CurrencyProps, CurrencySettings, ModelRow } from "@mcleod/core";
import { ExpandableFilterController, getDispatchControlBoolean } from "@mcleod/general";
import { AutogenLayoutQuoteRates } from "./autogen/AutogenLayoutQuoteRates";
import { OtherChargesUtil } from "./OtherChargesUtil";
import { RateUtil } from "./RateUtil";

export class QuoteRates extends AutogenLayoutQuoteRates {
    private estimateFuelSurcharge: boolean = getDispatchControlBoolean("est_fuel_surcharge");
    private autoRate: boolean = getDispatchControlBoolean("auto_rate");
    private calculatingCharges: boolean = false;
    private expandableFilter: ExpandableFilterController = new ExpandableFilterController();
    private otherChargesUtil: OtherChargesUtil = new OtherChargesUtil();

    onLoad() {
        this.textboxRateType.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            const newValue = event.selection.value;
            this.activeRow.set(event.target.field, newValue);
            if (newValue == "O") {
                this.textboxRateUnits.enabled = true;
                this.textboxRateUnits.enabledDuringAdd = true;
                this.textboxRateUnits.enabledDuringUpdate = true;
            } else {
                this.textboxRateUnits.enabled = false;
                this.textboxRateUnits.enabledDuringAdd = false;
                this.textboxRateUnits.enabledDuringUpdate = false;
            }
        });

        this.buttonEstimateFuelSurcharge.visible = !this.estimateFuelSurcharge;
        this.setupAdditionalChargeExpandableFilter();
        this.otherChargesUtil.initialize(this);
        this.textboxCurrencyType.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();
    }

    setupAdditionalChargeExpandableFilter() {
        this.expandableFilter.initialize(this.panelOtherChargesHeader2, this.panelOtherChargeTable);
    }

    buttonRateOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        this.calculateRates(true).finally(() => {
            button.busy = false
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && this.otherChargesUtil.currencyType) {
                this.convertPricingCurrencies(this.otherChargesUtil.currencyType);
            }
        });
    }

    async calculateRates(autorate: boolean = this.autoRate, estimateFsc: boolean = this.estimateFuelSurcharge, overrideRateId?: number) {
        if (!this.mainDataSource.isAddingOrUpdating() || this.calculatingCharges || this.mainDataSource.activeRow == null) return Promise.resolve();
        this.mainDataSource.setComponentsBusy(true);
        this.calculatingCharges = true;

        try {
            const orderRow = this.mainDataSource.getDataboundValues(null, true, null, null);
            orderRow.set("customer_id", this.activeRow.get("customer_id"));
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                if (this.otherChargesUtil.currencyType && this.activeRow.getCurrency("freight_charge").getCurrencyCode() !==
                    this.otherChargesUtil.currencyType) {
                    this.convertPricingCurrencies(this.otherChargesUtil.currencyType)
                }
                orderRow.set("freight_charge", this.activeRow.get("freight_charge"));
            }
            const response = await Api.search("lme/dispatch/calculate-order-charges", {
                "order_type": "Q",
                "estimate_fsc": estimateFsc,
                "autorate": autorate,
                "order_row": orderRow,
                "calculate_max_target": false
            });
            const data = response.data[0];
            const orderApiData = data?.["order_row"] ?? [];
            if (estimateFsc) {
                this.calculateOtherCharges(data);
            }
            this.activeRow.set("rate_type", orderApiData["rate_type"]);
            this.activeRow.set("bill_distance", orderApiData["bill_distance"]);
            this.activeRow.set("autorate_status", orderApiData["autorate_status"]);
            this.activeRow.set("rate_id", overrideRateId ?? orderApiData["rate_id"]);
            this.activeRow.set("rate", orderApiData["rate"]);
            this.activeRow.set("rate_units", orderApiData["rate_units"]);
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && orderApiData["freight_charge_c"] !=
                this.otherChargesUtil.currencyType) {
                const tempCurrency: Currency = new Currency(orderApiData["freight_charge"] as CurrencyProps);
                tempCurrency.setCurrencyCode(this.otherChargesUtil.currencyType);
                this.activeRow.set("freight_charge", tempCurrency);
            }
            else {
                this.activeRow.set("freight_charge", orderApiData["freight_charge"]);
            }
            this.activeRow.set("otherchargetotal", await this.otherChargesUtil.getOtherChargeTotal(false));
            if (orderApiData["total_charge"] != null) {
                if (orderApiData["total_charge"].amount != this.activeRow.get("total_charge")?.amount || 0) {
                    this.activeRow.set("total_charge", orderApiData["total_charge"]);
                }
            }
            RateUtil.setAutoRateContext(this, this.activeRow.get("rate_id"), this.activeRow.get("orig_dest_rate_id"));
        }
        finally {
            this.mainDataSource.setComponentsBusy(false);
            this.calculatingCharges = false;
        }
    }

    private calculateOtherCharges(data) {
        const appendingId: string[] = [];
        // Estimate Fuel Surcharge
        this.tableOtherCharges.rows.forEach(row => {
            if (row.data?._appending == false) appendingId.push(row.data?._data["id"])
        });
        for (let i = this.tableOtherCharges.rows.length - 1; i >= 0; i--) {
            const row = this.tableOtherCharges.rows[i];
            if (row?.data?.data["est_fuel_surcharge"] === "Y") {
                row.deleteRow()
            }
        }

        this.sourceOrderOtherChargeQuote.data = [];
        const otherCharges = data?.["other_charges"] ?? [];
        otherCharges.forEach((element, index) => {
            const isAppending = appendingId.length == 0 || !appendingId.includes(element["id"]);
            const modelRow = new ModelRow(this.sourceOrderOtherChargeQuote.url, isAppending);
            modelRow.setValues(element);
            this.sourceOrderOtherChargeQuote.addRow(modelRow, index, false);
        });
        this.tableOtherCharges.displayData(null, this.sourceOrderOtherChargeQuote.data, 0);
    }

    textboxCurrencyTypeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selectedRow = event.selection;
        if (selectedRow) {
            const currencyType = selectedRow.get("currency_type_id", CurrencySettings.getTransactionalCurrency());
            this.convertPricingCurrencies(currencyType);
        }
    }

    convertPricingCurrencies(currencyType: string) {
        this.otherChargesUtil.currencyType = currencyType;
        const freightCharge: Currency = this.mainDataSource.activeRow?.getCurrency("freight_charge");
        freightCharge?.setCurrencyCode(currencyType);
        const otherCharge: Currency = this.mainDataSource.activeRow?.getCurrency("otherchargetotal");
        otherCharge?.setCurrencyCode(currencyType);
        const totalCharge: Currency = this.mainDataSource.activeRow?.getCurrency("total_charge");
        totalCharge?.setCurrencyCode(currencyType);
        this.mainDataSource.activeRow?.setValues({ "freight_charge": freightCharge, "otherchargetotal": otherCharge, "total_charge": totalCharge});
        this.mainDataSource.activeRow?.set("freight_charge_c", currencyType);
        this.mainDataSource.activeRow?.setLookupModelData("freight_charge_c", { currency_type_id: currencyType });
        this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyType);
        this.tableOtherCharges.rows.forEach((tableRow) => {
            const modelRow: ModelRow = tableRow.data;
            const amount: Currency = modelRow.getCurrency("amount");
            if (amount) {
                amount.setCurrencyCode(currencyType);
                modelRow.set("amount", amount);
                tableRow.displayComponentValues();
            }
        });
        const quickAdd: TableRow = this.tableOtherCharges.quickAddRow;
        if (quickAdd) {
            const quickModelRow: ModelRow = quickAdd.data;
            const amount: Currency = quickModelRow.getCurrency("amount");
            if (amount) {
                amount.setCurrencyCode(currencyType);
                quickModelRow.set("amount", amount);
                quickAdd.displayComponentValues();
            }
        }
    }

    /// START - Other Charge Logic ///
    getOtherChargeDatasource(): DataSource {
        return this.sourceOrderOtherChargeQuote;
    }

    async otherChargeOnBlur(event: BlurEvent) {
        await this.otherChargesUtil.otherChargeOnBlur(event);
    }

    async tableOtherChargesOnContentsChanged() {
        await this.otherChargesUtil.tableOtherChargesOnContentsChanged();
    }

    async textboxChargeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        await this.otherChargesUtil.textboxChargeIdOnLookupModelSelection(event);
    }
    /// END - Other Charge Logic ///

    sourceQuoteOrderOnDisplay(event: DataDisplayEvent) {
        if (this.activeRow)
            RateUtil.setAutoRateContext(this, this.activeRow.get("rate_id"), this.activeRow.get("orig_dest_rate_id"));
    }

    sourceQuoteOrderAfterModeChange(event: DataSourceModeChangeEvent) {
        RateUtil.setRateListeners(this);
        if (event.newMode === DataSourceMode.ADD) {
            this.sourceOrderOtherChargeQuote.mode = DataSourceMode.ADD; // Trigger creation of blank row and set QUICK ADD to ADD mode
        }
    }

    sourceQuoteOrderAfterExecution() {
        RateUtil.enableDisableUnitsAndRates(this, this.activeRow.get("rate_type"));
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            if (this.mainDataSource.mode !== DataSourceMode.ADD) {
                const quoteFreightCharge = this.mainDataSource.activeRow?.getCurrency("freight_charge");
                const quoteCurrencyCode = quoteFreightCharge?.getCurrencyCode();
                this.mainDataSource.activeRow?.setLookupModelData("freight_charge_c", { currency_type_id: quoteCurrencyCode });
                this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyType);
                this.convertPricingCurrencies(quoteCurrencyCode);
            }
        }
    }

    async sourceOrderOtherChargeQuoteAfterExecution() {
        if (this.sourceOrderOtherChargeQuote.mode === DataSourceMode.UPDATE) {
            if (this.activeRow.isNull("otherchargetotal") || this.activeRow.isNull("total_charge")) {
                await this.otherChargesUtil.tableOtherChargesOnContentsChanged();
            }
        }
    }
}
